import { ArrowUturnLeftIcon, ReceiptRefundIcon } from '@heroicons/react/24/outline'
import { useCallback, useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { reprintPayableCheck, undoPayoutReturn } from 'services'
import { PlainTable } from 'stories/components'
import { confirm, thousandSeperatorWithPrefix } from 'utils'

import { ReturnPayoutModal } from '../ReturnPayoutModal'

/**
 * A payee's cross-loan payment history — everything paid to them through the check queue, whichever
 * loan it came from. This is the payable equivalent of a participant's participation history: the
 * participation figures (ownership %, principal, interest, fees, servicing fee) have no meaning for
 * a vendor or contractor, so the columns are the ones a payable actually carries.
 *
 * Shared by the vendor and contractor screens — they differ only in which payee they load.
 */
export const PayableHistory = ({ title, load }: { title?: string; load: () => Promise<any> }) => {
  const [payments, setPayments] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [returning, setReturning] = useState<any | null>(null)

  const fetchPayments = useCallback(() => {
    setLoading(true)
    load()
      .then((res: any) => setPayments(Array.isArray(res?.payments) ? res.payments : []))
      .catch(() => {})
      .finally(() => setLoading(false))
    // `load` is supplied by the caller; it changes when the payee does.
  }, [load])

  useEffect(() => {
    fetchPayments()
  }, [fetchPayments])

  /**
   * Void the check/ACH reference on a paid item and put it back into the check queue so it can be
   * reissued — for a lost, spoiled, or misprinted check.
   */
  const onReprint = async (p: any) => {
    const message = (
      <div className="text-gray-600 mb-4 text-left">
        Return <span className="font-semibold">{p.referenceNumber}</span> to the check queue?
        <p className="text-sm mt-2">
          The reference number is cleared and {thousandSeperatorWithPrefix(Number(p.amount) || 0)} goes back into the
          queue to be paid again. The original payout run keeps a record of the voided number.
        </p>
      </div>
    )
    if (!(await confirm(message))) return
    const res: any = await reprintPayableCheck(p.id)
    if (res?.success) {
      toast(res.message || 'Returned to the check queue.', { type: 'success' })
      fetchPayments()
    } else {
      toast(res?.message || 'Could not reprint this check.', { type: 'error' })
    }
  }

  /** Take back a return keyed in by mistake: the deposit goes and the payment stands as paid. */
  const onUndoReturn = async (p: any) => {
    const message = (
      <div className="text-gray-600 mb-4 text-left">
        Undo the return of <span className="font-semibold">{p.referenceNumber}</span>?
        <p className="text-sm mt-2">
          The deposit that booked the money coming back is removed and this goes back to paid. A reissue of it that has
          not been paid yet is voided.
        </p>
      </div>
    )
    if (!(await confirm(message))) return
    const res: any = await undoPayoutReturn(p.id)
    if (res?.success) {
      toast(res.message || 'Return undone.', { type: 'success' })
      fetchPayments()
    } else {
      toast(res?.message || 'Could not undo this return.', { type: 'error' })
    }
  }

  return (
    <div className="mt-4">
      {!!title && <p className="mb-1 font-medium">{title}</p>}
      <div className="relative max-h-72 overflow-auto">
        <PlainTable
          header={['No', 'Date', 'Loan #', 'Amount', 'Category', 'Memo', 'Check / Ref #', '']}
          tdClass="px-4 py-2 whitespace-nowrap"
          data={
            payments.length
              ? payments.map((p, index) => [
                  index + 1,
                  `${p.status === 'voided' ? '(voided) ' : ''}${
                    p.status === 'returned' ? '(returned) ' : ''
                  }${p.paidDate || p.createdAt?.slice(0, 10) || ''}`,
                  p.loanNumber ?? '',
                  thousandSeperatorWithPrefix(Number(p.amount) || 0),
                  p.ledgerCategory || '',
                  p.memo || '',
                  p.referenceNumber ? (
                    <span>
                      {p.referenceNumber}
                      <span className="text-gray-400 text-[11px] ml-1 uppercase">{p.paymentMethod}</span>
                    </span>
                  ) : p.status === 'queued' ? (
                    <span className="text-xs text-gray-500">in check queue</span>
                  ) : (
                    ''
                  ),
                  // Reprint is only meaningful once a number has been assigned. Returned sits
                  // beside it and means the opposite: reprint undoes a payment that never got
                  // cashed, a return keeps the payment and books the money coming back.
                  p.referenceNumber && p.status === 'paid' ? (
                    <div className="flex items-center gap-3 text-xs">
                      <button
                        type="button"
                        className="text-shade-blue hover:underline inline-flex items-center gap-1"
                        onClick={() => onReprint(p)}
                      >
                        <ArrowUturnLeftIcon className="w-4 h-4" /> Reprint
                      </button>
                      <button
                        type="button"
                        className="text-orange-700 hover:underline inline-flex items-center gap-1"
                        onClick={() => setReturning(p)}
                      >
                        <ReceiptRefundIcon className="w-4 h-4" /> Returned
                      </button>
                    </div>
                  ) : p.status === 'returned' ? (
                    <button
                      type="button"
                      className="text-shade-blue hover:underline inline-flex items-center gap-1 text-xs"
                      onClick={() => onUndoReturn(p)}
                    >
                      <ArrowUturnLeftIcon className="w-4 h-4" /> Undo return
                    </button>
                  ) : (
                    ''
                  ),
                ])
              : [[loading ? '…' : '—', loading ? 'Loading…' : 'No payments yet', '', '', '', '', '', '']]
          }
        />
      </div>

      {!!returning && (
        <ReturnPayoutModal
          payout={returning}
          onClose={() => setReturning(null)}
          onDone={() => {
            setReturning(null)
            fetchPayments()
          }}
        />
      )}
    </div>
  )
}
